import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { ZurueckZurSuche } from "@/components/search/ZurueckZurSuche"
import { Search, User, Unlink, CalendarClock, Check, PhoneIncoming, PhoneOutgoing, ListChecks, NotebookPen, Plus } from "lucide-react"
import { api } from "@/lib/api"
import { Loading, ErrorBox, fmtRelative } from "@/components/portal/LoadingError"
import { StatusBadge } from "@/components/portal/StatusBadge"
import { StatusActions } from "@/components/portal/StatusActions"
import { CreateContactModal } from "@/components/portal/CreateContactModal"
import { CallTaskModal } from "@/components/portal/CallTaskModal"
import { InterneAufgabeModal } from "@/components/portal/InterneAufgabeModal"
import { HeroInfoPopover } from "@/components/portal/HeroInfoPopover"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import { useContactDraftRestore } from "@/hooks/useContactDraftRestore"
import { cn } from "@/lib/utils"
import { anrufAufgabenText } from "@/lib/anrufAufgabe"
import { kontaktName } from "@/lib/heroSearch"
import { wegSchritt } from "@/lib/wege"

interface LinkSearchResult {
  id: number
  name: string
  email: string
  phone: string
  address: string
}

/** Wie `/api/v1/contacts/search` antwortet. Firmenkontakte tragen ihren
 *  Namen NUR in `company_name` — dafuer gibt es `kontaktName()`. */
interface ContactSearchItem {
  id: number
  first_name?: string | null
  last_name?: string | null
  company_name?: string | null
  email?: string | null
  phone?: string | null
  address?: string | null
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(body.error || body.detail || `HTTP ${res.status}`)
  return body as T
}

/**
 * Sipgate-Anruf-Detail mit HERO-Link + Booking.
 *
 * 🔴 Alles hier laeuft ueber `/api/v1/sipgate/...`. Die alten
 * `/admin/sipgate/...`-Routen wurden am 07.05.2026 entfernt; wer sie ruft,
 * bekommt von FastAPI ein 404 mit `{"detail":"Not Found"}` — und weil
 * `fetchJson` genau dieses Feld anzeigt, stand am 02.09.2026 ein nacktes
 * „Not Found" ueber dem Termin-Formular. Es ist der dritte Rueckfall
 * derselben Sorte (20.05. Buchung, 15.06. Verknuepfen im HeroLinkModal) —
 * deshalb bewacht `SipgateCallDetailPfade.test.tsx` diese Datei jetzt.
 */
export default function SipgateCallDetail() {
  const { id } = useParams<{ id: string }>()
  const callId = Number(id)
  const qc = useQueryClient()

  const pageRestore = useContactDraftRestore(callId)

  const { data, isLoading, error } = useQuery({
    queryKey: ["sipgate-call", id],
    // Einzelabruf statt Suche in den neuesten 500 (01.11.2026): ein aelterer
    // Anruf aus der Suche fehlte dort, `find` lieferte undefined und React
    // Query brach mit „data is undefined" ab. 404 → „nicht gefunden".
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/sipgate/calls/{call_id}", {
        params: { path: { call_id: callId } },
      })
      if (response.status === 404) return null
      if (error) throw new Error(String(error))
      return data ?? null
    },
    enabled: !!id,
  })

  if (isLoading) return <Loading label="Lädt Anruf..." />
  if (error) return <div className="p-10"><ErrorBox error={error} /></div>
  if (!data) return <div className="p-10 text-muted-foreground">Anruf #{callId} nicht gefunden.</div>

  const c = data
  // HERO-Bezeichnung des Vorgangs aus dem lokalen Projekt-Cache — von Hand
  // ergaenzt, weil `api-types.ts` generiert ist.
  const vorgang = c as typeof c & { hero_project_nr?: string | null; hero_project_title?: string | null }
  const callPhone = c.direction === "out" ? c.to : c.from
  const onInvalidate = () => qc.invalidateQueries({ queryKey: ["sipgate-call", id] })

  return (
    <div className="px-10 py-8 max-w-[1100px]">
      {pageRestore.restoreOpen && pageRestore.draft && (
        <CreateContactModal
          open={true}
          onClose={pageRestore.consume}
          mode="sipgate"
          itemId={callId}
          onSuccess={onInvalidate}
          draftId={pageRestore.draft.id}
          draftPrefill={pageRestore.draft}
          initialWithProject={pageRestore.draft.withProject}
          projectOnly={pageRestore.draft.projectOnly}
        />
      )}
      {/* Aus der Suche gekommen? Dann dorthin zurueck, sonst in die Liste. */}
      <ZurueckZurSuche ersatz={{ to: "/sipgate", label: "Zurück zur Anruf-Liste" }} />

      <div className="rounded-lg border bg-card shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b bg-secondary">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-[18px] font-bold flex items-center gap-2">
                {c.direction === "out" ? (
                  <PhoneOutgoing className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <PhoneIncoming className="w-4 h-4 text-primary" />
                )}
                <span data-pii>{callPhone}</span>
              </h1>
              <div className="text-[13px] text-muted-foreground mt-1.5 space-y-0.5">
                <div>Kanal: {c.channel_name} · Tenant: {c.tenant_slug}</div>
                {c.bot_angenommen && !c.weiterleitung_id && !c.transfer_von_tenant && (
                  <div>Der KI-Bot hat das Gespräch geführt.</div>
                )}
                {c.weiterleitung_id && (
                  <div>
                    {(c.transfer_grund || "").startsWith("Direkt-Weiterleitung") ? "Direkt an werkXpartner durchgestellt" : "KI-Bot hat an werkXpartner durchgestellt"}
                    {c.weiterleitung_status === "missed" ? " · niemand abgenommen" : c.weiterleitung_status === "completed" ? " · angenommen" : ""}
                    {c.transfer_grund && !(c.transfer_grund || "").startsWith("Direkt-Weiterleitung") ? ` · ${c.transfer_grund}` : ""}
                  </div>
                )}
                {c.transfer_von_tenant && !c.weiterleitung_id && (
                  <div>
                    {(c.transfer_grund || "").startsWith("Direkt-Weiterleitung") ? "Direkt-Weiterleitung von" : "Weiterleitung vom KI-Bot"} {c.transfer_von_tenant_name || c.transfer_von_tenant}
                    {c.transfer_anrufer ? <> · Anrufer <span data-pii>{c.transfer_anrufer}</span></> : ""}
                    {c.transfer_grund ? ` · ${c.transfer_grund}` : ""}
                  </div>
                )}
                <div>Zeit: {fmtRelative(c.created_at)}</div>
                {c.hero_contact_name && <div><User className="w-3 h-3 inline" /> HERO: <span data-pii>{c.hero_contact_name}</span></div>}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <StatusBadge status={c.status} />
              <StatusActions
                current={c.status}
                options={["answered", "missed", "done"]}
                labels={{ answered: "Angenommen", missed: "Verpasst", done: "Erledigt" }}
                mutate={async (s) => {
                  const { error } = await api.PATCH("/api/v1/sipgate/calls/{call_id}", {
                    params: { path: { call_id: c.id } },
                    body: { status: s },
                  })
                  if (error) throw new Error(String(error))
                }}
                invalidateKeys={[["sipgate-calls"], ["sipgate-call", id]]}
                size="sm"
              />
            </div>
          </div>
        </div>

        {/* KI-Zusammenfassung */}
        {(c.ai_headline || c.ai_summary) && (
          <div className="px-6 py-4 border-b">
            <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              KI-Zusammenfassung
            </div>
            {c.ai_headline && <div className="text-[14px] font-medium" data-pii>{c.ai_headline}</div>}
            {c.ai_summary && <div className="text-[13px] text-muted-foreground whitespace-pre-wrap mt-1" data-pii>{c.ai_summary}</div>}
          </div>
        )}

        {/* HERO-Verknuepfung */}
        <HeroLinkSection
          callId={c.id}
          heroContactId={c.hero_contact_id}
          heroContactName={c.hero_contact_name}
          heroProjectId={c.hero_project_id}
          heroProjectNr={vorgang.hero_project_nr}
          heroProjectTitel={vorgang.hero_project_title}
          tenantSlug={c.tenant_slug}
          onChange={() => qc.invalidateQueries({ queryKey: ["sipgate-call", id] })}
        />

        {/* Booking */}
        <BookingSection
          callId={c.id}
          heroContactId={c.hero_contact_id}
          heroBookingId={c.hero_booking_id}
          defaultTitle={c.hero_contact_name || callPhone}
          onBooked={() => qc.invalidateQueries({ queryKey: ["sipgate-call", id] })}
        />

        {/* Aufgabe erstellen */}
        <TaskSection
          callId={c.id}
          tenantSlug={c.tenant_slug}
          defaultTitle={c.ai_headline || (c.hero_contact_name ? `Rückruf: ${c.hero_contact_name}` : `Rückruf: ${callPhone}`)}
          defaultComment={anrufAufgabenText({
            headline: c.ai_headline,
            summary: c.ai_summary,
            kontaktName: c.hero_contact_name,
            telefon: callPhone,
          })}
        />
      </div>

    </div>
  )
}

function TaskSection({
  callId, tenantSlug, defaultTitle, defaultComment,
}: {
  callId: number
  tenantSlug: string
  defaultTitle: string
  defaultComment?: string
}) {
  const [open, setOpen] = useState(false)
  const [internOpen, setInternOpen] = useState(false)
  return (
    <div className="px-6 py-4 border-t">
      <div className="flex items-center justify-between">
        <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">Aufgabe</div>
        <div className="flex items-center gap-2">
          {/* 🔑 Zwei Wege, bewusst getrennt benannt (#39): die eine Aufgabe
              geht nach HERO und vor den Monteur, die andere bleibt im Portal.
              Wer das verwechselt, schickt eine Buero-Notiz an den Kunden. */}
          <button
            onClick={() => setInternOpen(true)}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md text-muted-foreground hover:bg-muted"
          >
            <NotebookPen className="w-3.5 h-3.5" /> Intern
          </button>
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20"
          >
            <ListChecks className="w-3.5 h-3.5" /> Aufgabe erstellen
          </button>
        </div>
      </div>
      <CallTaskModal
        open={open}
        onClose={() => setOpen(false)}
        mode="sipgate"
        callId={callId}
        tenantSlug={tenantSlug}
        defaultTitle={defaultTitle}
        defaultComment={defaultComment}
      />
      {/* Mit `callId`: die Aufgabe traegt den Rueckweg zum Anruf (#39). */}
      <InterneAufgabeModal
        open={internOpen}
        onClose={() => setInternOpen(false)}
        tenantSlug={tenantSlug || null}
        callId={callId}
        titelVorschlag={defaultTitle || null}
      />
    </div>
  )
}

function HeroLinkSection({
  callId, heroContactId, heroContactName, heroProjectId, heroProjectNr, heroProjectTitel, tenantSlug, onChange,
}: {
  callId: number
  heroContactId?: number | null
  heroContactName?: string | null
  heroProjectId?: string | null
  heroProjectNr?: string | null
  heroProjectTitel?: string | null
  tenantSlug?: string | null
  onChange: () => void
}) {
  const [q, setQ] = useState(heroContactName || "")
  const [results, setResults] = useState<LinkSearchResult[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [createProjectOpen, setCreateProjectOpen] = useState(false)

  const search = useMutation({
    mutationFn: async () => {
      const d = await fetchJson<{ items: ContactSearchItem[] }>(
        `/api/v1/contacts/search?tenant=${encodeURIComponent(tenantSlug || "")}`
        + `&q=${encodeURIComponent(q)}`,
      )
      return (d.items || []).map((c): LinkSearchResult => ({
        id: c.id,
        name: kontaktName(c),
        email: c.email || "",
        phone: c.phone || "",
        address: c.address || "",
      }))
    },
    onSuccess: (treffer) => setResults(treffer),
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  const link = useMutation({
    mutationFn: (c: LinkSearchResult) => fetchJson(`/api/v1/sipgate/${callId}/hero-link`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contact_id: c.id, contact_name: c.name }),
    }),
    onSuccess: () => { wegSchritt("kontakt_verknuepft"); onChange(); setResults([]); setErr(null) },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  const unlink = useMutation({
    // null = leeren. Derselbe Endpunkt wie beim Setzen, anderer Rumpf.
    mutationFn: () => fetchJson(`/api/v1/sipgate/${callId}/hero-link`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contact_id: null, contact_name: null, project_id: null }),
    }),
    onSuccess: onChange,
  })

  return (
    <div className="px-6 py-4 border-b">
      <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        HERO-Verknüpfung
      </div>

      {heroContactId && (
        <div className="p-3 rounded-md bg-primary/5 border border-primary/20 mb-3 flex items-center justify-between">
          <div>
            <HeroInfoPopover tenantSlug={tenantSlug || ""} contactId={heroContactId} projectId={heroProjectId}>
              <div className="text-[14px] font-medium flex items-center gap-1.5 cursor-help">
                <User className="w-3.5 h-3.5" />
                {heroContactName || `Kontakt #${heroContactId}`}
              </div>
            </HeroInfoPopover>
            {heroProjectId && (
              <HeroInfoPopover tenantSlug={tenantSlug || ""} contactId={heroContactId} projectId={heroProjectId}>
                <div className="text-[12px] text-muted-foreground mt-0.5 cursor-help">
                  <HeroVorgangName tenantSlug={tenantSlug} projectId={heroProjectId} nr={heroProjectNr} titel={heroProjectTitel} />
                </div>
              </HeroInfoPopover>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => setCreateProjectOpen(true)}
              className="inline-flex items-center gap-1 text-[11.5px] text-primary hover:underline"
              title="Neues Projekt fuer diesen Kontakt anlegen"
            >
              <Plus className="w-3 h-3" /> Projekt anlegen
            </button>
            <button
              onClick={() => unlink.mutate()}
              disabled={unlink.isPending}
              className="inline-flex items-center gap-1 text-[11.5px] text-destructive hover:underline disabled:opacity-50"
            >
              <Unlink className="w-3 h-3" /> Trennen
            </button>
          </div>
        </div>
      )}
      {createProjectOpen && (
        <CreateContactModal
          open={true}
          onClose={() => setCreateProjectOpen(false)}
          mode="sipgate"
          itemId={callId}
          tenantSlug={tenantSlug || undefined}
          onSuccess={() => { setCreateProjectOpen(false); onChange() }}
          projectOnly
        />
      )}

      {err && <div className="p-2 rounded-md bg-destructive/10 text-destructive text-[12px] mb-2">{err}</div>}

      {!heroContactId && <NeuKontaktButton callId={callId} tenantSlug={tenantSlug || undefined} onChange={onChange} />}

      <form
        onSubmit={(ev) => { ev.preventDefault(); setErr(null); search.mutate() }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Kontakt im Cache suchen (min 2 Zeichen)..."
            className="w-full h-9 pl-8 pr-3 text-[13px] rounded-md border bg-background"
          />
        </div>
        <button
          type="submit"
          disabled={search.isPending || q.length < 2}
          className="text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {search.isPending ? "..." : "Suchen"}
        </button>
      </form>

      {results.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {results.map((r) => (
            <button
              key={r.id}
              onClick={() => link.mutate(r)}
              disabled={link.isPending}
              className="w-full text-left p-2.5 rounded-md border hover:border-primary hover:bg-primary/5 transition"
            >
              <div className="text-[13.5px] font-medium" data-pii>{r.name}</div>
              <div className="text-[11.5px] text-muted-foreground" data-pii>
                {[r.email, r.phone, r.address].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function NeuKontaktButton({ callId, tenantSlug, onChange }: { callId: number; tenantSlug?: string; onChange: () => void }) {
  const [open, setOpen] = useState(false)
  const { restoreOpen, draft, consume } = useContactDraftRestore(callId)
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="sg-btn sg-btn-hero-green"
        style={{ background: "#10b981", color: "#fff", borderColor: "#10b981", marginBottom: 10 }}
      >
        + Neuen HERO-Kontakt anlegen
      </button>
      <CreateContactModal
        open={open || restoreOpen}
        onClose={() => { setOpen(false); consume() }}
        mode="sipgate"
        itemId={callId}
        tenantSlug={tenantSlug}
        onSuccess={onChange}
        draftId={draft?.id}
        draftPrefill={draft || undefined}
        initialWithProject={draft?.withProject}
        projectOnly={draft?.projectOnly}
      />
    </>
  )
}

/** Export nur fuer den Waechter-Test der Kategorie-Vorauswahl. */
export function BookingSection({
  callId, heroContactId, heroBookingId, defaultTitle, onBooked,
}: {
  callId: number
  heroContactId?: number | null
  heroBookingId?: string | null
  defaultTitle: string
  onBooked: () => void
}) {
  const [open, setOpen] = useState(false)
  const [slot, setSlot] = useState("")
  const [duration, setDuration] = useState("2")
  const [title, setTitle] = useState(defaultTitle)
  const [description, setDescription] = useState("")
  const [categoryId, setCategoryId] = useState<string>("")
  const [partnerIds, setPartnerIds] = useState<number[]>([])
  const [err, setErr] = useState<string | null>(null)

  const opts = useQuery<{ categories: any[]; partners: any[]; monteure: any[]; resources: any[]; default_category_id?: number | null }>({
    queryKey: ["sipgate-calendar-options", callId],
    queryFn: () => fetchJson(`/api/v1/sipgate/${callId}/calendar-options`),
    enabled: open && !!heroContactId,
    staleTime: 1000 * 60 * 5,
  })

  // 🔴 Kategorie vorwaehlen — die des Mandanten, NICHT die erste aus HEROs
  // Liste. Ohne Vorauswahl stand hier "– Kategorie –", und direkt darunter
  // liegt bei Sanitec "BLOCKER". Die Kategorie entscheidet, in welchen Status
  // der Vorgang nach dem Termin rutscht (`appointment_step_map_by_type`):
  // KLI-827 wurde am 28.07.2026 aus diesem Dialog als BLOCKER gebucht, danach
  // auf "Umsetzung" korrigiert — und der Auto-Status schob den Beratungstermin
  // am 19.08. auf "Rechnung erstellen" statt "Angebot erstellen".
  useEffect(() => {
    if (!open || categoryId) return
    const kategorien = opts.data?.categories
    if (!kategorien?.length) return
    const vorgabe = opts.data?.default_category_id
    const bekannt = vorgabe != null && kategorien.some((c: any) => c.id === vorgabe)
    if (bekannt) setCategoryId(String(vorgabe))
  }, [open, opts.data, categoryId])

  const book = useMutation({
    mutationFn: () => fetchJson(`/api/v1/sipgate/${callId}/hero-book`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        start_datetime: slot ? new Date(slot).toISOString() : null,
        // "tag3" -> 3 ganze Tage; sonst Stunden (#13).
        ...(duration.startsWith("tag")
          ? { ganztags_tage: parseInt(duration.slice(3), 10) }
          : { duration_hours: parseFloat(duration) }),
        title,
        description,
        category_id: categoryId ? parseInt(categoryId) : null,
        partner_ids: partnerIds,
        resource_ids: [],
      }),
    }),
    onSuccess: () => { wegSchritt("termin_gebucht"); setOpen(false); onBooked() },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          Termin
        </div>
        {heroBookingId ? (
          <span className="inline-flex items-center gap-1 text-[12px] text-success font-semibold">
            <Check className="w-3 h-3" /> Gebucht #{heroBookingId}
          </span>
        ) : (
          <button
            onClick={() => setOpen(!open)}
            disabled={!heroContactId}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
            title={!heroContactId ? "Erst HERO-Kontakt verknüpfen" : ""}
          >
            <CalendarClock className="w-3.5 h-3.5" /> Termin anlegen
          </button>
        )}
      </div>

      {open && !heroBookingId && (
        <div className="mt-3 p-3 rounded-md border bg-secondary/30 space-y-3">
          {err && <div className="p-2 rounded-md bg-destructive/10 text-destructive text-[12px]">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Termin</label>
              <input type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-[13px]" />
            </div>
            <div>
              <label className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Dauer</label>
              <select value={duration} onChange={(e) => setDuration(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-[13px]">
                <option value="1">1 Stunde</option>
                <option value="1.5">1,5 Stunden</option>
                <option value="2">2 Stunden</option>
                <option value="3">3 Stunden</option>
                <option value="4">4 Stunden</option>
                <option value="6">6 Stunden</option>
                <option value="8">8 Stunden</option>
                {/* Ganztaegig ueber mehrere Tage (#13). Dieselbe Liste wie im
                BookingModal — sie fehlte hier, deshalb liess sich aus der
                Anrufansicht kein mehrtaegiger Termin anlegen. */}
                <option value="tag2">2 Tage (ganztägig)</option>
                <option value="tag3">3 Tage (ganztägig)</option>
                <option value="tag4">4 Tage (ganztägig)</option>
                <option value="tag5">5 Tage (ganztägig)</option>
                <option value="tag10">10 Tage (ganztägig)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Titel</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-[13px]" />
          </div>
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Kategorie</label>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-[13px]">
              <option value="">– Kategorie –</option>
              {opts.data?.categories?.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {(opts.data?.partners?.length || opts.data?.monteure?.length) && (
            <div>
              <label className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Mitarbeiter</label>
              <div className="max-h-24 overflow-y-auto border rounded-md p-2 space-y-1 bg-background">
                {[...(opts.data?.partners || []), ...(opts.data?.monteure || [])].filter((p: any, i: number, arr: any[]) => arr.findIndex((x) => x.id === p.id) === i).map((p: any) => (
                  <label key={p.id} className="flex items-center gap-2 text-[12px] cursor-pointer">
                    <input type="checkbox" checked={partnerIds.includes(p.id)} onChange={(e) => setPartnerIds(e.target.checked ? [...partnerIds, p.id] : partnerIds.filter((x) => x !== p.id))} />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Beschreibung</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optionale Beschreibung"
              className="w-full rounded-md border bg-background px-2 py-1.5 text-[13px]"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setOpen(false)} className="text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/70">Abbrechen</button>
            <button
              disabled={!slot || book.isPending}
              onClick={() => { setErr(null); book.mutate() }}
              className={cn(
                "text-[12.5px] font-medium px-4 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center gap-1.5",
                (!slot || book.isPending) && "opacity-50 cursor-not-allowed",
              )}
            >
              <Check className="w-3.5 h-3.5" /> {book.isPending ? "Bucht..." : "Buchen"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
